import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Grid,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../../api/client';

const { Title } = Typography;

const Conceptos = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/conceptos', {
        params: { page, limit, search: searchDebounced || undefined },
      });
      setData(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar conceptos');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchDebounced]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ nombre: '', muestraEnGrilla: true });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      nombre: row.nombre,
      muestraEnGrilla: row.muestraEnGrilla !== false,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const nombre = v.nombre.trim();
      const muestraEnGrilla = v.muestraEnGrilla !== false;
      if (editing) {
        await api.put(`/conceptos/${editing.id}`, { nombre, muestraEnGrilla });
        message.success('Concepto actualizado');
      } else {
        await api.post('/conceptos', { nombre, muestraEnGrilla });
        message.success('Concepto creado');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: 'Eliminar concepto',
      content: `¿Eliminar "${row.nombre}"?`,
      okText: 'Eliminar',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/conceptos/${row.id}`);
          message.success('Concepto eliminado');
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 72, responsive: ['sm'] },
    { title: 'Nombre del concepto', dataIndex: 'nombre', ellipsis: true },
    {
      title: 'Muestra en grilla',
      dataIndex: 'muestraEnGrilla',
      width: 130,
      align: 'center',
      render: (v) => (v !== false ? <Tag color="green">Si</Tag> : <Tag>No</Tag>),
    },
    {
      title: '',
      key: 'acciones',
      width: 88,
      fixed: isNarrow ? 'right' : undefined,
      render: (_, row) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} />
        </Space>
      ),
    },
  ];

  return (
    <Card
      style={{ background: '#ffffff', borderColor: '#d8e8dc', maxWidth: '100%' }}
      styles={{ body: { padding: isNarrow ? 12 : 16 } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: isNarrow ? 'column' : 'row',
            alignItems: isNarrow ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: 12,
            width: '100%',
          }}
        >
          <Title level={5} style={{ margin: 0, color: '#198039' }}>
            Conceptos
          </Title>
          <Space
            direction={isNarrow ? 'vertical' : 'horizontal'}
            style={{ width: isNarrow ? '100%' : 'auto' }}
            size="small"
          >
            <Input.Search
              allowClear
              placeholder="Buscar..."
              style={{ width: isNarrow ? '100%' : 220 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} block={isNarrow}>
              Nuevo
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            showTotal: (t) => `${t} registros`,
            size: isNarrow ? 'small' : 'default',
            simple: isNarrow,
          }}
          scroll={{ x: isNarrow ? 420 : 760 }}
          size="small"
        />
      </Space>

      <Modal
        title={editing ? 'Editar concepto' : 'Nuevo concepto'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        destroyOnClose
        okText="Guardar"
        width={isNarrow ? 'calc(100vw - 16px)' : 440}
        centered
      >
        <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
          <Form.Item name="nombre" label="Nombre del concepto" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="Ej: Flete interno" maxLength={255} showCount />
          </Form.Item>
          <Form.Item name="muestraEnGrilla" label="Mostrar en grilla" valuePropName="checked">
            <Switch checkedChildren="Si" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Conceptos;
